/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { MouseEvent, useMemo, useState, useEffect } from 'react';
import { Dropdown, OverlayTrigger, Popover } from 'react-bootstrap';
import styled from 'styled-components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Flex, Label, Paragraph } from '@motili/ui';

import Config from 'Config';
import { shouldAllowManageAsset } from 'contractor/services/ContractorWorkOrdersService';
import { useAppSelector } from 'common/stores/redux/hooks';
import EditPhotoRequirementsModal from 'admin/components/work-orders/photoRequirements/EditPhotoRequirementsModal';
import { displayDateTime } from 'common/utils/date-time';

import UploadPhotoRequireButton from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploadedRow/UploadPhotoRequireButton';
import { DocumentRequirementsPhotosUploadDrawer } from 'common/components/document/DocumentRequirementsPhotosDrawer';
import {
    DOCUMENT_TYPE,
    QUOTE_STATUS,
    WORK_ORDER_STATUS,
    WorkOrderPhotoTypeAPI,
    WorkOrderRequirementApprovalReasonAPI,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { DisplayType } from 'common/types';
import {
    AcceptedFile,
    Document,
    LhsRiskType,
    PhotoTypeRequirement,
    QuotePhotoTypeRequirement,
    Photo,
    APPROVAL_STATUS,
} from 'common/types/documentRequirements';

import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import FormTextView from 'common/components/form/FormTextView';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { PreviewContainer } from 'common/components/FilePreview';
import {
    KebabMenuButton,
    KebabMenuIcon,
} from 'common/components/buttons/ActionIconButtons/KebabMenuButton';
import TableActionsDropdown from 'common/components/table/TableActionsDropdown';
import { getAllowFileUpload } from 'common/components/file-upload/FileUploadTypes';
import {
    isVideo,
    photosSequenceSortFunc,
} from '../DocumentRequirementsPhotosUploaded';
import {
    openVideoInNewTab,
    VideoMask,
    isCompleteVideo,
    getMediaCategory,
    getShowKebab,
    isIncompleteVideo,
    getIsActionDisabledFromStatus,
    RejectedCtaWrap,
    RejectedActionsWrap,
} from './WorkOrderRequirementsPhotosUploadedRow';
import styles from '../../DocumentRequirementsPhotos.module.scss';
import { FormElement, FormRow } from '../../../form/FormLayout';
import MotiliTextButton from '../../../buttons/MotiliTextButton';
import { getNotApplicationFiles } from '../../../file-upload/FileUploadDropzone';
import PhotoCardShell from '../../Shared/PhotoCardShell';
import PhotoRequirementEmptyCard from '../../Shared/PhotoRequirementEmptyCard';
import PhotoViewDetailsDrawer from '../../Shared/PhotoViewDetailsDrawer';
import RejectedPhotoActions from '../../Shared/RejectedPhotoActions';
import { StyledChip } from '../../Shared/StyledChip';

const InfoRow = styled(FormRow)`
    margin: 0 !important;
    & > div:first-child {
        flex: 2;
    }
`;
const MoreMenuIcon = styled(MoreHorizOutlinedIcon)`
    font-size: 36px !important;
`;

const FileLink = styled.a`
    display: block;
    width: 100%;
    height: 100%;
    outline: none;

    &:focus,
    &:focus-visible {
        outline: none;
    }

    &:hover,
    &:active,
    &:visited,
    &:focus {
        color: ${Config.color.motiliLightBlue};
        text-decoration: none;
    }
`;

const NotApplicableButtonWrap = styled.div`
    width: 158px;
    height: 40px;

    & button {
        width: 100% !important;
        height: 100% !important;
    }
`;

const RejectedCtaDrawerWrap = styled.div`
    margin-top: 12px;
    display: flex;
    width: 100%;
    justify-content: flex-end;
    gap: 8px;
    align-items: center;
`;

const UploadButtonWrap = styled.div`
    width: 120px;
    height: 40px;

    & div,
    & div[role='presentation'] {
        height: 100% !important;
    }

    & button {
        width: 100% !important;
        height: 100% !important;
        min-height: 40px !important;
    }
`;

const BusyOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 4;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledPreviewContainer = styled(PreviewContainer)<{ $h: number }>`
    padding: 0 !important;
    border: 0 !important;
    background: #000;
    border-radius: 6px;
    overflow: hidden;

    width: 100%;
    height: ${({ $h }) => `${$h}px`};
    position: relative;

    a {
        display: flex;
        width: 100%;
        height: 100%;
        align-items: center;
        justify-content: center;
    }

    a > * {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    a i,
    a svg {
        margin: auto;
    }

    img,
    video {
        width: 100% !important;
        height: 100% !important;
        object-fit: contain;
        display: block;
    }
`;

const ContractorRejectBox = styled.div`
    margin-top: 10px;
    padding: 0;
    height: auto;
    max-height: none;
    overflow: visible;
    word-break: break-word;
`;

const StatusPill = styled.div<{ status?: string }>`
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 400;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    ${({ status }) => {
        switch (status) {
            case 'APPROVE':
                return `
                    background: ${Config.color.fadedGreen};
                    color: ${Config.color.green};
                    border: 1px solid ${Config.color.green};
            `;
            case 'REJECT':
                return `
                    background: ${Config.color.lightRed};
                    color: ${Config.color.red};
                    border: 1px solid ${Config.color.red};
             `;
            case 'ACCEPT':
                return `
                    background: ${Config.color.white};
                    color: ${Config.color.darkCharcoal};
                    border: 1px solid ${Config.color.inputBorderGray};
            `;
            default:
                return `
                background: ${Config.color.fadedGray};
                color: ${Config.color.darkGray2};
                border: 1px solid ${Config.color.mediumLightGray};
            `;
        }
    }}
`;

const NoUppercaseMenuItem = styled.div`
    .no-uppercase {
        text-transform: none !important;
    }

    .remove-red,
    .remove-red a,
    .remove-red span {
        color: #e63232 !important;
    }

    .dropdown-menu > li > a,
    .dropdown-item,
    .no-uppercase {
        text-align: left !important;
        justify-content: flex-start !important;
        font-weight: 700 !important;
    }
`;

const ViewDetailsLink = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    color: ${Config.color.motiliLightBlue};
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
`;

const ContractorActionsWrap = styled.div`
    padding: 12px 0 0 0;
    & > div > div {
        gap: 8px !important;
    }

    & > div > div > div:first-child button {
        width: 158px !important;
        height: 40px !important;
    }

    & > div > div > div:last-child button {
        width: 120px !important;
        height: 40px !important;
    }
`;

interface DocumentRequirementsPhotosUploadedRowProps {
    photos: Photo[];
    photo: Photo;
    photoTypes?: WorkOrderPhotoTypeAPI[];
    disabled?: boolean;
    removeAcls?: boolean;
    handleRemovePhotoOnClick: () => Promise<void> | void;
    handleEditUploadedPhotoRequirement: (
        photoId: number,
        photoTypeId: string,
        description: string,
        requiresPullAndClean: boolean
    ) => void;
    photoTypeRequirements: PhotoTypeRequirement[];
    quotePhotoTypeRequirements: QuotePhotoTypeRequirement[];
    document: Document;
    authenticationData: {
        authenticatedUserIsOps: boolean;
        authenticatedUserIsCon: boolean;
        authenticatedUserCanEdit: boolean;
    };
    workOrderRequirementApprovalReasons?: WorkOrderRequirementApprovalReasonAPI[];
    lhsRiskTypes?: LhsRiskType[];
    handleEditOnClick: () => void;
    handleCloseOnClick: () => void;
    hasAdditionalNote: (photo: Photo) => boolean | string;
    renderAdditionalNote: (photo: Photo) => JSX.Element;
    renderImage: () => JSX.Element | null;
    handleMetadataOnClick: (e: MouseEvent<HTMLDivElement>) => void;
    photoRequirementMet: (photo: Photo) => boolean;
    showReUpload: (photo: Photo) => boolean;
    handleReUpload: (
        description: string,
        requiresPullAndClean: boolean
    ) => void;
    showEditPhotoRequirementsModal: boolean;
    busy: boolean;
    acceptedFiles: AcceptedFile[];
    action?: { display: string };
    handleOnDrop: (
        acceptedFiles: AcceptedFile[],
        rejectedFiles: File[],
        actionType: 'upload' | 'reupload'
    ) => void;
    handleCloseUpload: () => void;
    hasApprovalStatus: (photo: Photo) => boolean;
    cardWidth?: number;
    mediaHeight?: number;
    videoDuration?: string;
}
type ContractorRejectDetails = {
    reasons?: Array<{
        id: string | number;
        text: string;
        additional?: string[];
    }>;
    rejectedComments?: string;
    videoDuration?: string;
};

function ContractorRejectBlock({
    contractorRejectDetails,
}: {
    contractorRejectDetails: ContractorRejectDetails | null;
}) {
    if (!contractorRejectDetails) return null;

    const { reasons, rejectedComments } = contractorRejectDetails;

    return (
        <div>
            {reasons?.length > 0 && (
                <ul
                    style={{
                        margin: 0,
                        paddingLeft: 18,
                        listStyleType: 'disc',
                    }}
                >
                    {reasons.map(r => (
                        <li key={r.id} style={{ marginBottom: 6 }}>
                            {r.text}
                            {r.additional?.length > 0 && (
                                <ul
                                    style={{
                                        marginTop: 6,
                                        paddingLeft: 18,
                                        listStyleType: 'disc',
                                    }}
                                >
                                    {r.additional.map(a => (
                                        <li key={`${r.id}-${a}`}>{a}</li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {Boolean(rejectedComments) && (
                <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        Rejected Comments
                    </div>
                    <div>{rejectedComments}</div>
                </div>
            )}
        </div>
    );
}

const DocumentRequirementsPhotosUploadedRow = ({
    photos,
    photo,
    photoTypes,
    disabled = false,
    removeAcls = false,
    handleRemovePhotoOnClick,
    handleEditUploadedPhotoRequirement,
    photoTypeRequirements,
    quotePhotoTypeRequirements,
    document,
    authenticationData,
    workOrderRequirementApprovalReasons,
    lhsRiskTypes,
    handleEditOnClick,
    handleCloseOnClick,
    hasAdditionalNote,
    renderAdditionalNote,
    renderImage,
    handleMetadataOnClick,
    photoRequirementMet,
    showReUpload,
    handleReUpload,
    showEditPhotoRequirementsModal,
    busy,
    acceptedFiles,
    action,
    handleOnDrop,
    handleCloseUpload,
    hasApprovalStatus,
    cardWidth,
    mediaHeight,
    videoDuration,
}: DocumentRequirementsPhotosUploadedRowProps) => {
    const { isMotiliAccount, isContractorAccount, token } = useAppSelector(
        state => state.authentication
    );
    const isContractorView = isContractorAccount;
    const accountId = token?.userId;

    const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
    const [isDrawerRemoving, setIsDrawerRemoving] = useState(false);

    const openEditFromViewDetails = () => {
        setIsViewDetailsOpen(false);
        window.setTimeout(() => {
            handleEditOnClick();
        }, 250);
    };
    const handleRemoveFromDrawer = async () => {
        setIsDrawerRemoving(true);
        try {
            await Promise.resolve(handleRemovePhotoOnClick());
        } finally {
            setIsDrawerRemoving(false);
            setIsViewDetailsOpen(false);
        }
    };

    useEffect(() => {
        setIsViewDetailsOpen(false);
    }, [photo?.id]);

    const issueTracking = useMemo(() => {
        if (
            photo.requirement?.photoTypeId === 'LIFE_HEALTH_SAFETY_RISK' &&
            photo.requirement?.exceptionFlagId
        ) {
            return document.issueTracking?.find(
                issue => issue.id === photo.requirement?.exceptionFlagId
            );
        }
        return undefined;
    }, [photo.requirement, document.issueTracking]);

    const disableActionIssueTracking = useMemo(() => {
        if (issueTracking) {
            return !issueTracking.active;
        }
        return false;
    }, [issueTracking]);

    const { riskType, riskReasons } = useMemo(() => {
        let riskType: LhsRiskType | undefined;
        let riskReasons: (string | undefined)[] | undefined;

        if (issueTracking) {
            riskType =
                lhsRiskTypes &&
                issueTracking.issueReasonId.length > 0 &&
                issueTracking.issueReasonId[0]
                    ? lhsRiskTypes.find(
                          rType => rType.id === issueTracking.issueReasonId[0]
                      )
                    : undefined;

            const _rawReasons =
                riskType &&
                [...(issueTracking?.issueSecondaryReasonId ?? [])]?.sort(
                    (a, b) => {
                        if (a === 'OTHER') return 1;
                        if (b === 'OTHER') return -1;
                        if (a < b) return -1;
                        if (a > b) return 1;
                        return 0;
                    }
                );
            riskReasons =
                riskType &&
                _rawReasons?.map(reason =>
                    reason === 'OTHER'
                        ? issueTracking.issueOtherReason!
                        : riskType!.reasons[`${reason}`]?.display
                );
        } else {
            riskType = lhsRiskTypes
                ? lhsRiskTypes.find(
                      rType => rType.id === document.workOrderLHS?.[0]?.riskType
                  )
                : undefined;
            const _rawReasons =
                riskType &&
                document.workOrderLHS &&
                [...(document.workOrderLHS[0]?.riskReasons ?? [])]?.sort(
                    (a, b) => {
                        if (a === 'OTHER') return 1;
                        if (b === 'OTHER') return -1;
                        if (a < b) return -1;
                        if (a > b) return 1;
                        return 0;
                    }
                );
            riskReasons =
                riskType &&
                _rawReasons?.map(reason =>
                    reason === 'OTHER'
                        ? document.workOrderLHS &&
                          document.workOrderLHS?.[0]?.otherReason
                        : riskType!.reasons[`${reason}`]?.display
                );
        }

        return { riskType, riskReasons };
    }, [issueTracking, lhsRiskTypes, document]);

    const photoType = (photoTypes || []).find(
        pt => pt.id === photo.requirement.photoTypeId
    );

    const photoIndexLabel = useMemo(() => {
        const siblings = (photos || []).filter(p => {
            const sameType =
                p?.requirement?.photoTypeId === photo?.requirement?.photoTypeId;
            const sameMediaCategory =
                getMediaCategory(p) === getMediaCategory(photo);
            return sameType && sameMediaCategory;
        });
        const sortedSiblings = siblings?.sort(photosSequenceSortFunc);
        const idx = sortedSiblings.findIndex(p => p?.id === photo?.id);

        const isDocument =
            photo?.requirement?.mimeType &&
            !photo.requirement.mimeType.startsWith('image/') &&
            !photo.requirement.mimeType.startsWith('video/');

        const label = isVideo(photo)
            ? 'Video'
            : isDocument
              ? 'Document'
              : 'Photo';
        return `${label} #${idx >= 0 ? idx + 1 : 1}`;
    }, [photos, photo]);

    const getContractorStatusText = (p: Photo) => {
        // no file will not show status
        if (!p?.requirement?.photo) return '';

        if (p?.approvalStatusId === 'APPROVE') return 'Approved';
        if (p?.approvalStatusId === 'REJECT') return 'Rejected';
        if (p?.approvalStatusId === 'ACCEPT') return 'Accepted';

        return '';
    };

    const contractorRejectDetails = useMemo(() => {
        if (photo?.approvalStatusId !== 'REJECT') return null;

        const approval = photo?.approval || {};
        const reasonIds: string[] = approval?.reasons || [];
        const additionalIds: string[] = approval?.additionalReasons || [];

        const reasons = (workOrderRequirementApprovalReasons || [])
            .filter(r => reasonIds.includes(r.id))
            .map(r => {
                const text =
                    r.id === 'OTHER' ? approval?.reasonOther : r.display;

                const additional =
                    (r.additionalReasons || [])
                        .filter(a => additionalIds.includes(a.id))
                        .map(a => a.display) || [];

                return { id: r.id, text, additional };
            })
            .filter(r => Boolean(r.text));

        const rejectedComments = approval?.additionalNotes || '';

        return { reasons, rejectedComments };
    }, [photo, workOrderRequirementApprovalReasons]);

    const disabledActionFromStatus = getIsActionDisabledFromStatus({
        document,
        isMotiliAccount,
        isContractorAccount,
        accountId,
    });

    const disableRemovePhotoRequirementStatus = disableRemovePhotoRequirement({
        document,
        isMotiliAccount,
        isContractorAccount,
    });

    const disabledReUpload = disablePhotoRequirementReUploadRejected({
        document,
        isMotiliAccount,
        isContractorAccount,
        approvalStatus: photo.approvalStatusId,
        accountId,
    });
    const disabledActions =
        disableRemovePhotoRequirementStatus ||
        !authenticationData.authenticatedUserCanEdit ||
        removeAcls === false ||
        disableActionIssueTracking;

    const actions: {
        id: string;
        onClick:
            | (() => void)
            | ((acceptedFiles: AcceptedFile[], rejectedFiles: File[]) => void);
        text: string;
        component: string;
        moStyle?: string;
        disabled?: boolean;
        busy?: boolean;
    }[] = [];

    const disableUploadActions = disabledActionFromStatus || busy;

    if (!photo.requirement.url) {
        actions.push(
            {
                id: 'upload-not-applicable',
                onClick: () =>
                    handleOnDrop(
                        getNotApplicationFiles() as AcceptedFile[],
                        [],
                        'upload'
                    ),
                disabled: disableUploadActions,
                text: 'Not Applicable',
                moStyle: 'normal',
                component: 'MotiliButton',
            },
            {
                id: 'upload',
                onClick: (
                    acceptedFiles: AcceptedFile[],
                    rejectedFiles: File[]
                ) => handleOnDrop(acceptedFiles, rejectedFiles, 'upload'),
                disabled: disableUploadActions,
                text: 'Upload',
                component: 'UploadPhotoRequireButton',
            }
        );
    }

    if (photo.requirement.url && showReUpload(photo) && !disabledReUpload) {
        actions.push(
            {
                id: 're-upload-not-applicable',
                onClick: () =>
                    handleOnDrop(
                        getNotApplicationFiles() as AcceptedFile[],
                        [],
                        'reupload'
                    ),
                disabled: busy,
                text: 'Not Applicable',
                moStyle: 'normal',
                component: 'MotiliButton',
            },
            {
                id: 're-upload',
                onClick: (
                    acceptedFiles: AcceptedFile[],
                    rejectedFiles: File[]
                ) => handleOnDrop(acceptedFiles, rejectedFiles, 'reupload'),
                disabled: busy,
                text: 'Re-Upload',
                component: 'UploadPhotoRequireButton',
            }
        );
    }

    if (photo.requirement.url && !disabledActions && removeAcls) {
        actions.push(
            {
                id: 'update-photo-type-button',
                onClick: handleEditOnClick,
                text: 'Update Photo Type',
                moStyle: 'normal',
                disabled: disabled || busy,
                component: 'MotiliButton',
            },
            {
                id: 'remove-photo-type-button',
                onClick: handleRemovePhotoOnClick,
                text: 'Remove',
                disabled: disabled || busy,
                moStyle: 'red',
                busy,
                component: 'MotiliButton',
            }
        );
    }

    const allowFiles = useMemo(
        () => getAllowFileUpload(document?.client?.enabledVideoRecording),
        [document]
    );

    const renderActionMenus = () => {
        if (actions.length === 0) {
            return undefined;
        }

        const showMenus = actions.slice(0, 2);

        return (
            <Flex direction='row' gap='16px'>
                {showMenus.map(m => {
                    if (m.component === 'UploadPhotoRequireButton') {
                        return (
                            <UploadPhotoRequireButton
                                key={m.id}
                                dataTestId={m.id}
                                handleOnDrop={
                                    m.onClick as (
                                        acceptedFiles: AcceptedFile[],
                                        rejectedFiles: File[]
                                    ) => void
                                }
                                disabled={(m.disabled || m.busy) as boolean}
                                text={m.text}
                                size='sm'
                                allowFiles={allowFiles}
                                buttonStyle={{
                                    width: '120px',
                                    height: '40px',
                                    minHeight: '40px',
                                }}
                            />
                        );
                    }
                    if (m.component === 'MotiliButton') {
                        return (
                            <MotiliButton
                                key={m.id}
                                dataTestId={m.id}
                                onClick={m.onClick as () => void}
                                text={m.text}
                                size='sm'
                                moStyle={
                                    m.moStyle as 'normal' | 'primary' | 'red'
                                }
                                disabled={m.disabled || m.busy}
                                busy={m.busy}
                            />
                        );
                    }
                    return undefined;
                })}

                {actions.length > 2 && (
                    <Dropdown
                        pullRight
                        title='table-action-dropdown'
                        disabled={disabled}
                        id='more-photo-require-actions'
                        data-test-id='more-photo-require-actions'
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <MotiliTextButton
                            text={<MoreMenuIcon />}
                            bsRole='toggle'
                            moStyle='action'
                            size='md'
                        />
                        <Dropdown.Menu>
                            {actions.slice(2).map((action, i) => (
                                <Dropdown.Item
                                    key={action.id}
                                    eventKey={i}
                                    style={{
                                        borderBottom: 'none',
                                    }}
                                    disabled={action.disabled}
                                    data-test-id={action.id}
                                >
                                    <span
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 700,
                                            color:
                                                action.moStyle === 'red'
                                                    ? Config.color.red
                                                    : undefined,
                                            textTransform: 'none',
                                        }}
                                    >
                                        {action.text}
                                    </span>
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </Flex>
        );
    };

    const DEFAULT_CARD_W = 312;
    const DEFAULT_MEDIA_H = 193;

    const resolvedCardW = cardWidth ?? DEFAULT_CARD_W;
    const resolvedMediaH = mediaHeight ?? DEFAULT_MEDIA_H;

    const { isLHS } = useMemo(() => {
        const isLHS =
            document?.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            photo?.requirement?.photoTypeId === 'LIFE_HEALTH_SAFETY_RISK';

        return {
            isLHS,
        };
    }, [document?.documentTypeId, photo?.requirement?.photoTypeId]);

    const hasUploaded = Boolean(photo?.requirement?.mimeType);

    const isRejected = photo?.approvalStatusId === 'REJECT';

    const allowContractorActionLHS = !(
        isContractorAccount &&
        isLHS &&
        (!issueTracking || !issueTracking.active)
    );

    const canReuploadAfterReject =
        isContractorView &&
        photo?.requirement?.url &&
        isRejected &&
        actions.length > 0 &&
        !disabledReUpload;

    const showKebab =
        allowContractorActionLHS &&
        getShowKebab({
            photo,
            disabled,
            disabledActionFromStatus: disableRemovePhotoRequirementStatus,
            authenticatedUserCanEdit:
                authenticationData.authenticatedUserCanEdit,
            removeAcls,
            busy,
        });

    const kebab = showKebab ? (
        <NoUppercaseMenuItem>
            <TableActionsDropdown
                title='Photo Requirement Table Actions'
                icon={<KebabMenuIcon />}
                actions={[
                    ...(!isIncompleteVideo(photo)
                        ? [
                              {
                                  name: 'Update Photo Type',
                                  onClick: () => openEditFromViewDetails(),
                                  acls: removeAcls,
                                  className: 'no-uppercase',
                              },
                          ]
                        : []),
                    {
                        name: 'Remove',
                        onClick: () => handleRemovePhotoOnClick(),
                        acls: removeAcls,
                        className: `${styles.remove} no-uppercase remove-red`,
                    },
                ]}
            />
        </NoUppercaseMenuItem>
    ) : null;

    const drawerKebab = showKebab ? (
        <NoUppercaseMenuItem>
            <TableActionsDropdown
                title='Photo Requirement Table Actions'
                icon={<KebabMenuIcon />}
                actions={[
                    ...(!isIncompleteVideo(photo)
                        ? [
                              {
                                  name: 'Update Photo Type',
                                  onClick: () => openEditFromViewDetails(),
                                  acls: removeAcls,
                                  className: 'no-uppercase',
                              },
                          ]
                        : []),
                    {
                        name: 'Remove',
                        onClick: () => handleRemoveFromDrawer(),
                        acls: removeAcls,
                        className: `${styles.remove} no-uppercase remove-red`,
                    },
                ]}
            />
        </NoUppercaseMenuItem>
    ) : null;

    const photoRequirementActions = useMemo(() => {
        if (!photo.requirement.url) {
            return (
                <RejectedCtaDrawerWrap>
                    <MotiliButton
                        dataTestId='upload-not-applicable'
                        text='Not Applicable'
                        moStyle='normal'
                        disabled={disableUploadActions}
                        style={{ width: 144, height: 36, whiteSpace: 'nowrap' }}
                        onClick={() =>
                            handleOnDrop(
                                getNotApplicationFiles() as AcceptedFile[],
                                [],
                                'upload'
                            )
                        }
                    />
                    <UploadPhotoRequireButton
                        dataTestId='upload'
                        handleOnDrop={(acceptedFiles, rejectedFiles) =>
                            handleOnDrop(acceptedFiles, rejectedFiles, 'upload')
                        }
                        disabled={disableUploadActions}
                        text='Upload'
                        allowFiles={allowFiles}
                        buttonStyle={{ width: 113, height: 36, padding: 0 }}
                    />
                </RejectedCtaDrawerWrap>
            );
        }

        if (photo.requirement.url && showReUpload(photo) && !disabledReUpload) {
            return (
                <RejectedCtaDrawerWrap>
                    <MotiliButton
                        dataTestId='re-upload-not-applicable'
                        text='Not Applicable'
                        moStyle='normal'
                        disabled={busy || disabledReUpload}
                        style={{ width: 144, height: 36, whiteSpace: 'nowrap' }}
                        onClick={() =>
                            handleOnDrop(
                                getNotApplicationFiles() as AcceptedFile[],
                                [],
                                'reupload'
                            )
                        }
                    />
                    <UploadPhotoRequireButton
                        dataTestId='re-upload'
                        handleOnDrop={(acceptedFiles, rejectedFiles) =>
                            handleOnDrop(
                                acceptedFiles,
                                rejectedFiles,
                                'reupload'
                            )
                        }
                        disabled={busy || disabledReUpload}
                        text='Re-Upload'
                        allowFiles={allowFiles}
                        buttonStyle={{ width: 113, height: 36, padding: 0 }}
                    />
                </RejectedCtaDrawerWrap>
            );
        }

        return null;
    }, [
        photo,
        showReUpload,
        disabledReUpload,
        busy,
        disableUploadActions,
        handleOnDrop,
        allowFiles,
    ]);
    return (
        <>
            <PhotoViewDetailsDrawer
                open={isViewDetailsOpen}
                onClose={() => setIsViewDetailsOpen(false)}
                mode='contractor'
                title={
                    photoType?.display ||
                    photo.requirement?.display ||
                    'View Details'
                }
                photo={photo}
                allPhotos={photos}
                renderImage={renderImage}
                headerRight={drawerKebab}
                isLHS={isLHS}
                riskType={riskType}
                riskReasons={riskReasons}
                photoRequirementMet={photoRequirementMet}
                hasApprovalStatus={hasApprovalStatus}
                workOrderRequirementApprovalReasons={
                    workOrderRequirementApprovalReasons
                }
                showMetadataLink={false}
                showApprovalDetails={false}
                showCheck={photoRequirementMet(photo)}
                photoIndexLabel={photoIndexLabel}
                requiresPullAndClean={Boolean(
                    photo.requirement?.requiresPullAndClean
                )}
                photoRequirementActions={photoRequirementActions}
                videoDuration={videoDuration}
                drawerBusy={isDrawerRemoving}
            />
            <DocumentRequirementsPhotosUploadDrawer
                handleCloseOnClick={handleCloseUpload}
                handleCancelOnClick={handleCloseUpload}
                handleUploadOnClick={({
                    description,
                    requiresPullAndClean,
                }: {
                    description: string;
                    requiresPullAndClean: boolean;
                }) => {
                    setIsViewDetailsOpen(false);
                    return handleReUpload(description, requiresPullAndClean);
                }}
                photoTypes={photoTypes}
                currentPhotoTypeId={photo.requirement.photoTypeId}
                photoTypeRequirements={photoTypeRequirements}
                photos={photos}
                file={acceptedFiles[0]}
                fileName={acceptedFiles[0]?.name}
                imgSrc={acceptedFiles[0]?.preview}
                mimeType={acceptedFiles[0]?.type}
                currentRequiresPullAndClean={
                    photo?.requirement?.requiresPullAndClean
                }
                action={action}
                open={acceptedFiles.length > 0}
            />

            {showEditPhotoRequirementsModal && (
                <EditPhotoRequirementsModal
                    key={`edit-photo-type-${photo?.id}-${photo?.requirement?.photoTypeId}`}
                    photos={photos || []}
                    selectedPhoto={photo}
                    currentPhotoTypeId={
                        photoType?.id || photo.requirement.photoTypeId
                    }
                    handleClose={handleCloseOnClick}
                    handleEditUploadedPhotoRequirement={async (
                        ...args: [number, string, string, boolean]
                    ) => {
                        await handleEditUploadedPhotoRequirement(...args);
                    }}
                    photoTypeRequirements={photoTypeRequirements}
                    quotePhotoTypeRequirements={quotePhotoTypeRequirements}
                    mimeType={photo.requirement.mimeType}
                    currentRequiresPullAndClean={
                        photo.requirement.requiresPullAndClean
                    }
                    renderImage={renderImage}
                    videoDuration={videoDuration}
                />
            )}
            {!hasUploaded ? (
                disabledActionFromStatus ? (
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            padding: '16px 8px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background:
                                'var(--color-super-light-gray, #F5F5F5)',
                            alignSelf: 'stretch',
                            color: Config.color.darkCharcoal,
                            fontSize: 18,
                            fontWeight: 600,
                        }}
                    >
                        No photos have been uploaded
                    </div>
                ) : (
                    <PhotoRequirementEmptyCard
                        dataTestId='photo-card-empty'
                        title='No photos have been uploaded'
                        hint='At least 1 Photo Required'
                        showHint
                        uploadAction={
                            <UploadButtonWrap>
                                <UploadPhotoRequireButton
                                    dataTestId='upload'
                                    allowFiles={allowFiles}
                                    handleOnDrop={(accepted, rejected) =>
                                        handleOnDrop(
                                            accepted,
                                            rejected,
                                            'upload'
                                        )
                                    }
                                    disabled={disableUploadActions}
                                    text='Upload'
                                    buttonStyle={{
                                        width: '100%',
                                        height: '100%',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                    }}
                                />
                            </UploadButtonWrap>
                        }
                        notApplicableAction={
                            <NotApplicableButtonWrap>
                                <MotiliButton
                                    dataTestId='upload-not-applicable'
                                    onClick={() =>
                                        handleOnDrop(
                                            getNotApplicationFiles() as AcceptedFile[],
                                            [],
                                            'upload'
                                        )
                                    }
                                    text='Not Applicable'
                                    moStyle='normal'
                                    disabled={disableUploadActions}
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                    }}
                                />
                            </NotApplicableButtonWrap>
                        }
                    />
                )
            ) : (
                <div
                    style={{
                        height: '100%',
                        boxShadow:
                            '0 0 0 1px #e6e6e6, 0 2px 8px rgba(0, 0, 0, 0.12)',
                    }}
                >
                    <PhotoCardShell
                        cardWidth={resolvedCardW}
                        mediaHeight={resolvedMediaH}
                        dataTestId='photo-card'
                        headerRight={
                            !isContractorView ? renderActionMenus() : undefined
                        }
                        kebab={null}
                        headerStyle={{ padding: 0, minHeight: 0 }}
                        bodyStyle={{ padding: 10 }}
                        media={
                            <StyledPreviewContainer $h={resolvedMediaH}>
                                <FileLink
                                    data-test-id='requirement-photo'
                                    href={
                                        isVideo(photo)
                                            ? undefined
                                            : photo.requirement.url
                                    }
                                    onClick={() => {
                                        if (isCompleteVideo(photo)) {
                                            openVideoInNewTab(photo);
                                        }
                                    }}
                                    style={{
                                        cursor:
                                            !isVideo(photo) ||
                                            isCompleteVideo(photo)
                                                ? 'pointer'
                                                : 'default',
                                    }}
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <VideoMask
                                        photo={photo}
                                        duration={videoDuration}
                                    />
                                    {renderImage()}
                                </FileLink>
                                {showKebab && kebab && (
                                    <KebabMenuButton>{kebab}</KebabMenuButton>
                                )}
                                {busy && !isDrawerRemoving && (
                                    <BusyOverlay>
                                        <i
                                            className='fa fa-spinner fa-spin'
                                            style={{
                                                fontSize: 28,
                                                color: '#fff',
                                            }}
                                        />
                                    </BusyOverlay>
                                )}
                            </StyledPreviewContainer>
                        }
                    >
                        {/* CONTRACTOR → ONLY PREVIEW */}
                        {isContractorView ? (
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <div>
                                        <Flex gap='8px' align='center'>
                                            <StyledChip
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                {photoIndexLabel}
                                            </StyledChip>

                                            {getContractorStatusText(photo) && (
                                                <StatusPill
                                                    status={
                                                        photo.approvalStatusId
                                                    }
                                                >
                                                    {getContractorStatusText(
                                                        photo
                                                    )}
                                                </StatusPill>
                                            )}
                                        </Flex>

                                        {photo.requirement
                                            ?.requiresPullAndClean && (
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: Config.color.red,
                                                    marginTop: 16,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Requires Pull and Clean
                                            </div>
                                        )}
                                    </div>

                                    <ViewDetailsLink
                                        type='button'
                                        onClick={() =>
                                            setIsViewDetailsOpen(true)
                                        }
                                    >
                                        View Details
                                    </ViewDetailsLink>
                                </div>
                                {photo.approvalStatusId === 'REJECT' && (
                                    <ContractorRejectBox>
                                        <ContractorRejectBlock
                                            contractorRejectDetails={
                                                contractorRejectDetails
                                            }
                                        />
                                    </ContractorRejectBox>
                                )}
                                {isContractorView && canReuploadAfterReject && (
                                    <RejectedCtaWrap>
                                        <RejectedActionsWrap>
                                            <RejectedPhotoActions
                                                mode='reupload'
                                                busy={busy}
                                                disabledNotApplicable={
                                                    busy || disabledReUpload
                                                }
                                                disabledUpload={
                                                    busy || disabledReUpload
                                                }
                                                handleOnDrop={handleOnDrop}
                                                notApplicableTestId='re-upload-not-applicable'
                                                uploadTestId='re-upload'
                                                uploadText='Re-Upload'
                                                allowFiles={allowFiles}
                                            />
                                        </RejectedActionsWrap>
                                    </RejectedCtaWrap>
                                )}
                            </>
                        ) : (
                            <>
                                {/* ADMIN/OPS → KEEP OLD DETAIL UI */}
                                <FormRow>
                                    <FormElement style={{ maxWidth: '120px' }}>
                                        <StyledPreviewContainer>
                                            <FileLink
                                                data-test-id='requirement-photo'
                                                href={
                                                    isVideo(photo)
                                                        ? undefined
                                                        : photo.requirement.url
                                                }
                                                onClick={() => {
                                                    if (isVideo(photo)) {
                                                        openVideoInNewTab(
                                                            photo
                                                        );
                                                    }
                                                }}
                                                target='_blank'
                                                rel='noreferrer'
                                            >
                                                <VideoMask
                                                    photo={photo}
                                                    duration={videoDuration}
                                                />
                                                {renderImage()}
                                            </FileLink>
                                        </StyledPreviewContainer>

                                        <div>
                                            {photo.photoMetadata && (
                                                <div
                                                    data-test-id='requirement-photo-meta-data'
                                                    style={{
                                                        color: Config.color
                                                            .motiliLightBlue,
                                                        cursor: 'pointer',
                                                        textDecoration:
                                                            'underline',
                                                    }}
                                                    onClick={
                                                        handleMetadataOnClick
                                                    }
                                                >
                                                    Photo metadata
                                                </div>
                                            )}
                                        </div>
                                    </FormElement>

                                    <FormElement>
                                        <InfoRow>
                                            <FormElement>
                                                <FormTextView
                                                    dataTestId='requirement-name'
                                                    label='Name'
                                                    value={
                                                        photo.requirement
                                                            .fileName
                                                    }
                                                />
                                            </FormElement>
                                            <FormElement>
                                                <FormTextView
                                                    dataTestId='requirement-uploaded-by'
                                                    label='Uploaded by'
                                                    value={
                                                        photo.requirement
                                                            .uploadedBy
                                                    }
                                                />
                                            </FormElement>
                                        </InfoRow>

                                        {isLHS ? (
                                            <>
                                                <InfoRow>
                                                    <FormElement>
                                                        <FormTextView
                                                            dataTestId='requirement-type'
                                                            label='Type'
                                                            value={
                                                                riskType?.display ||
                                                                ''
                                                            }
                                                        />
                                                    </FormElement>
                                                    <FormElement>
                                                        <FormTextView
                                                            dataTestId='requirement-uploaded-on'
                                                            label='Uploaded on'
                                                            value={displayDateTime(
                                                                photo.created
                                                            )}
                                                        />
                                                    </FormElement>
                                                </InfoRow>
                                                <InfoRow>
                                                    <FormElement>
                                                        <FormTextView
                                                            dataTestId='requirement-reason'
                                                            label='Reason'
                                                            value={
                                                                riskReasons?.join(
                                                                    '\n'
                                                                ) || ''
                                                            }
                                                        />
                                                    </FormElement>
                                                </InfoRow>
                                            </>
                                        ) : (
                                            <InfoRow>
                                                <FormElement>
                                                    <div data-test-id='requirement-description'>
                                                        <Label label='Description' />
                                                        <Paragraph>
                                                            {
                                                                photo
                                                                    .requirement
                                                                    .description
                                                            }
                                                        </Paragraph>
                                                    </div>
                                                </FormElement>
                                                <FormElement>
                                                    <FormTextView
                                                        dataTestId='requirement-uploaded-on'
                                                        label='Uploaded on'
                                                        value={displayDateTime(
                                                            photo.created
                                                        )}
                                                    />
                                                </FormElement>
                                            </InfoRow>
                                        )}

                                        {photo.requirement
                                            ?.requiresPullAndClean && (
                                            <InfoRow>
                                                <FormElement>
                                                    <div
                                                        data-test-id='requires-pull-and-clean'
                                                        style={{
                                                            fontSize: 16,
                                                            color: Config.color
                                                                .red,
                                                        }}
                                                    >
                                                        Requires Pull and Clean
                                                    </div>
                                                </FormElement>
                                            </InfoRow>
                                        )}
                                    </FormElement>

                                    <FormElement
                                        style={{
                                            maxWidth: '300px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {photoRequirementMet(photo) &&
                                            hasApprovalStatus(photo) && (
                                                <ApprovalDetails
                                                    photo={photo}
                                                    approvalReasons={
                                                        workOrderRequirementApprovalReasons
                                                    }
                                                    onlyShowRejectReasons
                                                />
                                            )}
                                    </FormElement>
                                </FormRow>

                                {hasAdditionalNote(photo) &&
                                    !(
                                        authenticationData.authenticatedUserIsCon &&
                                        (isApproved(photo) || isAccepted(photo))
                                    ) && <>{renderAdditionalNote(photo)}</>}
                            </>
                        )}
                    </PhotoCardShell>
                </div>
            )}
        </>
    );
};

interface DisablePhotoParams {
    document: Document;
    isMotiliAccount: boolean;
    isContractorAccount: boolean;
    accountId?: number;
}

const disableStatus = [
    WORK_ORDER_STATUS.COMPLETED,
    WORK_ORDER_STATUS.CLOSED,
    WORK_ORDER_STATUS.CANCELLED,
];

const disableRemovePhotoRequirement = (params: DisablePhotoParams) => {
    const {
        document,
        isMotiliAccount = false,
        isContractorAccount = false,
    } = params;

    if (isMotiliAccount) {
        return (
            document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            disableStatus.includes(document.statusId as WORK_ORDER_STATUS)
        );
    }
    if (isContractorAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const allowStatus = [WORK_ORDER_STATUS.IN_PROCESS];
            return !allowStatus.includes(
                document.statusId as WORK_ORDER_STATUS
            );
        }
        if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
            const allowStatus = [QUOTE_STATUS.REVISING];
            return !allowStatus.includes(document.statusId as QUOTE_STATUS);
        }
    }
    return true;
};

const disablePhotoRequirementReUploadRejected = (
    params: DisablePhotoParams & {
        approvalStatus?: string;
    }
) => {
    const {
        document,
        isMotiliAccount = false,
        isContractorAccount = false,
        approvalStatus = '',
        accountId,
    } = params;

    if (isMotiliAccount) {
        return (
            document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            disableStatus.includes(document.statusId as WORK_ORDER_STATUS)
        );
    }

    if (isContractorAccount) {
        return (
            document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            (disableStatus.includes(document.statusId as WORK_ORDER_STATUS) ||
                (document.statusId === WORK_ORDER_STATUS.IN_REVIEW &&
                    approvalStatus !== 'REJECT') ||
                !shouldAllowManageAsset(document, accountId))
        );
    }
    return true;
};

const isAccepted = (photo: Photo) => {
    return photo.approvalStatusId === 'ACCEPT';
};

const isApproved = (photo: Photo) => {
    return photo.approvalStatusId === 'APPROVE';
};

interface ApprovalDetailsProps {
    photo: Photo;
    approvalReasons?: WorkOrderRequirementApprovalReasonAPI[];
    onlyShowRejectReasons?: boolean;
}

function ApprovalDetails({
    photo,
    approvalReasons = [],
    onlyShowRejectReasons = false,
}: ApprovalDetailsProps) {
    const displayReasons = useMemo(() => {
        if (!photo?.approval) return [];
        const reasons = photo.approval.reasons || [];
        const additionalReasons = photo.approval.additionalReasons || [];
        return approvalReasons
            .filter(reason => reasons.includes(reason.id))
            .map(reason => ({
                id: reason.id,
                display:
                    reason.id === 'OTHER'
                        ? photo.approval!.reasonOther
                        : reason.display,
                additionalReasons: (reason.additionalReasons || []).filter(
                    additional => additionalReasons.includes(additional.id)
                ),
            }));
    }, [approvalReasons, photo]);

    const status = APPROVAL_STATUS[
        photo.approvalStatusId as keyof typeof APPROVAL_STATUS
    ] || {
        color: 'unset',
        display: 'UNKNOWN',
        newUIDisplay: 'Unknown',
    };

    const showReasons =
        !onlyShowRejectReasons || photo.approvalStatusId === 'REJECT';
    return (
        <div data-test-id='approval-details'>
            <Label style={{ color: status.color, fontSize: 16 }}>
                {status.newUIDisplay}
            </Label>
            <div className={styles.woPhotoApprovalDetails}>
                {showReasons &&
                    displayReasons.map(reason => (
                        <Flex direction='row' gap='4px' key={reason.id}>
                            <div>{reason.display}</div>
                            <AdditionalReasonsInfo
                                reasons={reason.additionalReasons}
                            />
                        </Flex>
                    ))}
            </div>
        </div>
    );
}

interface AdditionalReasonsInfoProps {
    reasons: DisplayType[];
}

function AdditionalReasonsInfo({ reasons }: AdditionalReasonsInfoProps) {
    const popover = useMemo(
        () =>
            reasons.length ? (
                <StyledPopover>
                    {reasons.map(reason => (
                        <li key={reason.id}>{reason.display}</li>
                    ))}
                </StyledPopover>
            ) : undefined,
        [reasons]
    );
    if (!popover) return null;
    return (
        <OverlayTrigger
            trigger='click'
            placement='left'
            overlay={popover}
            rootClose
        >
            <InfoIcon />
        </OverlayTrigger>
    );
}

const StyledPopover = styled(Popover)`
    min-width: 180px;
    max-width: 400px;
    border-color: transparent;
    border-radius: 0;
    box-shadow: 0 2px 6px 0 #00000040;
    padding: 12px;

    & .popover-content {
        padding: 0 12px;
    }

    & .arrow {
        display: none;
    }
`;
const InfoIcon = styled(InfoOutlinedIcon)`
    cursor: pointer;
    font-size: 24px !important;
    color: ${Config.color.motiliLightBlue};
`;

export default DocumentRequirementsPhotosUploadedRow;

export { ApprovalDetails };
